// ===========================================
// Parent Bookings Page
// ===========================================
// Two-column layout:
// - Left: Narrow tab section for booking actions
// - Right: Unified chronological bookings card

import { useState, useMemo } from 'react';
import { format, isAfter, isBefore, startOfDay } from 'date-fns';
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Grid,
} from '@mui/material';
import {
  Event as EventIcon,
  Autorenew as MakeupIcon,
  AddCircleOutline as ExtraIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import IndividualBookingTab from '../../components/booking/IndividualBookingTab';
import MakeupBookingTab from '../../components/booking/MakeupBookingTab';
import ExtraLessonTab from '../../components/booking/ExtraLessonTab';
import {
  useMyBookings,
} from '../../hooks/useHybridBooking';
import {
  useMakeupHistory,
  useExtraLessonRequests,
  useExtraLessonSettings,
} from '../../hooks/useBooking';
import {
  HybridBooking,
  getBookingStatusColor,
  formatTimeSlot,
  canModifyBooking,
} from '../../api/hybridBooking.api';
import { MakeupSlot, ExtraLessonRequest } from '../../api/booking.api';
import { parseUTCDate } from '../../utils/dateUtils';

// ===========================================
// TYPES
// ===========================================

type BookingType = 'individual' | 'makeup' | 'extra';

interface UnifiedBooking {
  id: string;
  type: BookingType;
  studentName: string;
  title: string;
  subtitle: string;
  date: Date;
  status: string;
  statusColor: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  canCancel: boolean;
  canReschedule: boolean;
  originalData: HybridBooking | MakeupSlot | ExtraLessonRequest;
}

// ===========================================
// TAB PANEL COMPONENT
// ===========================================

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`booking-tabpanel-${index}`}
      aria-labelledby={`booking-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `booking-tab-${index}`,
    'aria-controls': `booking-tabpanel-${index}`,
  };
}

// ===========================================
// HELPER FUNCTIONS
// ===========================================

const getExtraLessonStatusColor = (status: string): UnifiedBooking['statusColor'] => {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'APPROVED':
      return 'info';
    case 'SCHEDULED':
      return 'primary';
    case 'COMPLETED':
      return 'success';
    case 'REJECTED':
      return 'error';
    case 'CANCELLED':
      return 'default';
    default:
      return 'default';
  }
};

const getBookingTypeIcon = (type: BookingType) => {
  switch (type) {
    case 'individual':
      return <EventIcon fontSize="small" />;
    case 'makeup':
      return <MakeupIcon fontSize="small" />;
    case 'extra':
      return <ExtraIcon fontSize="small" />;
  }
};

const getBookingTypeLabel = (type: BookingType) => {
  switch (type) {
    case 'individual':
      return 'Individual';
    case 'makeup':
      return 'Make-up';
    case 'extra':
      return 'Extra';
  }
};

// ===========================================
// COMPONENT
// ===========================================

export default function BookingsPage() {
  const [activeTab, setActiveTab] = useState(0);

  // Queries for unified bookings list
  const { data: individualBookings, isLoading: individualLoading } = useMyBookings();
  const { data: makeupHistory, isLoading: makeupLoading } = useMakeupHistory();
  const { data: extraRequests, isLoading: extraLoading } = useExtraLessonRequests();
  const { data: extraLessonSettings, isLoading: settingsLoading } = useExtraLessonSettings();

  // Debug log
  console.log('Extra Lesson Settings:', extraLessonSettings, 'Loading:', settingsLoading);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Unify all bookings into a single chronological list
  const unifiedBookings = useMemo(() => {
    const bookings: UnifiedBooking[] = [];
    const now = startOfDay(new Date());

    // Add individual hybrid bookings
    (individualBookings ?? []).forEach((booking) => {
      if (booking.status === 'CANCELLED') return;

      const bookingDate = parseUTCDate(booking.scheduledDate);
      const canModify = canModifyBooking(booking.scheduledDate, booking.startTime);

      bookings.push({
        id: booking.id,
        type: 'individual',
        studentName: `${booking.Student?.firstName || ''} ${booking.Student?.lastName || ''}`.trim(),
        title: booking.Lesson?.name || 'Individual Session',
        subtitle: `Week ${booking.weekNumber} | ${formatTimeSlot(booking.startTime, booking.endTime)}`,
        date: bookingDate,
        status: booking.status,
        statusColor: getBookingStatusColor(booking.status),
        canCancel: canModify && booking.status !== 'COMPLETED',
        canReschedule: canModify && booking.status !== 'COMPLETED',
        originalData: booking,
      });
    });

    // Add make-up bookings
    (makeupHistory ?? []).forEach((slot) => {
      if (!slot.isBooked) return;

      const slotDate = parseUTCDate(slot.date);
      const isPast = isBefore(slotDate, now);

      bookings.push({
        id: slot.id,
        type: 'makeup',
        studentName: `${slot.Student?.firstName || ''} ${slot.Student?.lastName || ''}`.trim(),
        title: slot.Instrument?.name || 'Make-up Lesson',
        subtitle: `${slot.startTime} - ${slot.endTime} | ${slot.Teacher?.User?.firstName || ''} ${slot.Teacher?.User?.lastName || ''}`.trim(),
        date: slotDate,
        status: isPast ? 'COMPLETED' : 'BOOKED',
        statusColor: isPast ? 'success' : 'primary',
        canCancel: !isPast,
        canReschedule: false,
        originalData: slot,
      });
    });

    // Add extra lesson requests (only scheduled ones show as bookings)
    (extraRequests ?? []).forEach((request) => {
      if (request.status === 'CANCELLED' || request.status === 'REJECTED') return;

      // For scheduled requests, use the scheduled date
      // For pending/approved, use created date for sorting but show status
      const requestDate = request.scheduledDate
        ? parseUTCDate(request.scheduledDate)
        : new Date(request.createdAt);

      bookings.push({
        id: request.id,
        type: 'extra',
        studentName: `${request.student?.firstName || ''} ${request.student?.lastName || ''}`.trim(),
        title: request.instrument?.name || 'Extra Lesson',
        subtitle: request.status === 'SCHEDULED' && request.scheduledTime
          ? `${request.scheduledTime} | ${request.duration || 45} mins`
          : `Requested ${format(new Date(request.createdAt), 'd MMM')}`,
        date: requestDate,
        status: request.status,
        statusColor: getExtraLessonStatusColor(request.status),
        canCancel: request.status === 'PENDING',
        canReschedule: false,
        originalData: request,
      });
    });

    // Sort by date ascending (upcoming first)
    return bookings.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [individualBookings, makeupHistory, extraRequests]);

  // Split into upcoming and past
  const { upcomingBookings, pastBookings } = useMemo(() => {
    const now = startOfDay(new Date());
    const upcoming: UnifiedBooking[] = [];
    const past: UnifiedBooking[] = [];

    unifiedBookings.forEach((booking) => {
      if (isAfter(booking.date, now) || booking.status === 'PENDING' || booking.status === 'APPROVED') {
        upcoming.push(booking);
      } else {
        past.push(booking);
      }
    });

    // Past bookings should be most recent first
    past.reverse();

    return { upcomingBookings: upcoming, pastBookings: past.slice(0, 5) };
  }, [unifiedBookings]);

  const isLoading = individualLoading || makeupLoading || extraLoading;

  return (
    <Box>
      <PageHeader
        title="Bookings"
        subtitle="Manage individual sessions, make-up lessons, and extra lesson requests"
      />

      <Grid container spacing={3}>
        {/* Left: Tab Section */}
        <Grid item xs={12} md={7} lg={8}>
          <Paper sx={{ mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="booking tabs"
              variant="fullWidth"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  minHeight: 56,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                },
              }}
            >
              <Tab
                icon={<EventIcon />}
                iconPosition="start"
                label="Individual"
                {...a11yProps(0)}
              />
              <Tab
                icon={<MakeupIcon />}
                iconPosition="start"
                label="Make-up"
                {...a11yProps(1)}
              />
              {extraLessonSettings?.enabled && (
                <Tab
                  icon={<ExtraIcon />}
                  iconPosition="start"
                  label="Extra"
                  {...a11yProps(2)}
                />
              )}
            </Tabs>
          </Paper>

          <TabPanel value={activeTab} index={0}>
            <IndividualBookingTab hideBookingsList />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <MakeupBookingTab hideBookingsList />
          </TabPanel>

          {extraLessonSettings?.enabled && (
            <TabPanel value={activeTab} index={2}>
              <ExtraLessonTab hideBookingsList />
            </TabPanel>
          )}
        </Grid>

        {/* Right: All Bookings Card */}
        <Grid item xs={12} md={5} lg={4}>
          <Card sx={{ position: 'sticky', top: 16 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                All Bookings
              </Typography>

              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : upcomingBookings.length === 0 && pastBookings.length === 0 ? (
                <Alert severity="info">
                  No bookings yet. Use the tabs on the left to create a booking.
                </Alert>
              ) : (
                <>
                  {/* Upcoming Section */}
                  {upcomingBookings.length > 0 && (
                    <>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Upcoming
                      </Typography>
                      <List dense disablePadding>
                        {upcomingBookings.map((booking) => (
                          <Paper
                            key={`${booking.type}-${booking.id}`}
                            variant="outlined"
                            sx={{ mb: 1 }}
                          >
                            <ListItem sx={{ py: 1 }}>
                              <Box sx={{ mr: 1.5, color: 'primary.main' }}>
                                {getBookingTypeIcon(booking.type)}
                              </Box>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" fontWeight={500}>
                                      {booking.title}
                                    </Typography>
                                    <Chip
                                      label={getBookingTypeLabel(booking.type)}
                                      size="small"
                                      variant="outlined"
                                      sx={{ height: 18, fontSize: '0.65rem' }}
                                    />
                                  </Box>
                                }
                                secondary={
                                  <>
                                    <Typography variant="caption" display="block">
                                      {format(booking.date, 'EEE, d MMM')} | {booking.subtitle}
                                    </Typography>
                                    {booking.studentName && (
                                      <Typography variant="caption" color="text.secondary">
                                        {booking.studentName}
                                      </Typography>
                                    )}
                                  </>
                                }
                              />
                              {booking.status !== 'CONFIRMED' && (
                                <ListItemSecondaryAction>
                                  <Chip
                                    label={booking.status}
                                    size="small"
                                    color={booking.statusColor}
                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                  />
                                </ListItemSecondaryAction>
                              )}
                            </ListItem>
                          </Paper>
                        ))}
                      </List>
                    </>
                  )}

                  {/* Past Section */}
                  {pastBookings.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Recent
                      </Typography>
                      <List dense disablePadding>
                        {pastBookings.map((booking) => (
                          <Paper
                            key={`${booking.type}-${booking.id}`}
                            variant="outlined"
                            sx={{ mb: 1, opacity: 0.7 }}
                          >
                            <ListItem sx={{ py: 0.5 }}>
                              <Box sx={{ mr: 1.5, color: 'text.secondary' }}>
                                {getBookingTypeIcon(booking.type)}
                              </Box>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" color="text.secondary">
                                    {booking.title}
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="caption" color="text.secondary">
                                    {format(booking.date, 'd MMM yyyy')}
                                  </Typography>
                                }
                              />
                              <Chip
                                label={booking.status}
                                size="small"
                                color={booking.statusColor}
                                sx={{ height: 18, fontSize: '0.6rem' }}
                              />
                            </ListItem>
                          </Paper>
                        ))}
                      </List>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
